/**
 * Block renderer — the last layer of the report pipeline.
 *
 * It switches on `kind` to pick a component and on `tone` to pick styling, and
 * that is the whole of its intelligence. It asks no questions about the plan:
 * every sentence arrives already written and already formatted, and every
 * cross-reference is resolved against the section skeleton before it gets here.
 *
 * If you find yourself adding a condition about a goal or a bond to this file,
 * it belongs in rules.ts instead.
 */

import React from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { Block, Section } from "../../../Helpers/portfolioPlanner/report/blocks";
import { resolveBody } from "../../../Helpers/portfolioPlanner/report/blocks";
import styles from "./portfolio-planner.module.css";

const TONE_ICON = {
  good: CheckCircleIcon,
  neutral: InfoOutlinedIcon,
  caution: WarningAmberIcon,
  critical: WarningAmberIcon,
} as const;

const TONE_CLASS = {
  good: styles.calloutGood,
  neutral: "",
  caution: styles.calloutWarn,
  critical: styles.calloutBad,
} as const;

const BlockView = ({
  block,
  sections,
}: {
  block: Block;
  sections: Section[];
}) => {
  const paragraphs = resolveBody(block.body, sections);
  if (!paragraphs.length && !block.title) return null;

  if (block.kind === "callout") {
    const Icon = TONE_ICON[block.tone];
    return (
      <div className={`${styles.blockCallout} ${TONE_CLASS[block.tone]}`}>
        <span className={styles.blockIcon} aria-hidden="true">
          <Icon style={{ fontSize: 16 }} />
        </span>
        {block.title ? <b className={styles.blockTitle}>{block.title}</b> : null}
        <div className={styles.blockBody}>
          {paragraphs.map((text) => (
            <p key={text}>{text}</p>
          ))}
        </div>
      </div>
    );
  }

  if (block.kind === "lead") {
    return (
      <div className={styles.blockLead}>
        {paragraphs.map((text) => (
          <p key={text}>{text}</p>
        ))}
      </div>
    );
  }

  if (block.kind === "list") {
    return (
      <ul className={styles.blockList}>
        {paragraphs.map((text) => (
          <li key={text}>{text}</li>
        ))}
      </ul>
    );
  }

  // table-note
  return (
    <div className={styles.blockNote}>
      {paragraphs.map((text) => (
        <p key={text}>{text}</p>
      ))}
    </div>
  );
};

/**
 * Renders every block belonging to one section — and, on the repeating
 * sections, to one subject. Returns null rather than an empty wrapper when
 * nothing fired, so a section with no findings leaves no gap.
 */
const ReportBlocks = ({
  blocks,
  sections,
  section,
  subjectId,
}: {
  blocks: Block[];
  sections: Section[];
  /** Omit to render the list exactly as given — used by the roll-up. */
  section?: Section["key"] | Section["key"][];
  subjectId?: number | string;
}) => {
  const keys = section
    ? Array.isArray(section)
      ? section
      : [section]
    : null;
  const mine = blocks.filter(
    (block) =>
      (keys === null || keys.includes(block.section)) &&
      (subjectId === undefined || block.subjectId === subjectId)
  );
  if (!mine.length) return null;

  return (
    <div className={styles.blockStack}>
      {mine.map((block) => (
        <BlockView
          key={`${block.id}-${block.subjectId ?? "plan"}`}
          block={block}
          sections={sections}
        />
      ))}
    </div>
  );
};

export default ReportBlocks;
